/* eslint-disable no-unused-vars */
/* eslint-disable max-lines */
import {
    Tabs, TabsContent, TabsList, TabsTrigger
} from 'shared/components/ui/tabs';
import React, {
    useRef, useState, useEffect
} from 'react';
import {
    Card, CardContent
} from 'shared/components/ui/card';
import {
    Button
} from 'shared/components/ui/button';
import {
    PageContent, PageLayout
} from 'shared/components/NewDesignComponents/PageLayout';
import {
    LayoutDashboard
} from 'lucide-react';
import {
    useNavigate, useLocation, useParams
} from 'react-router-dom';
import {
    bexInsightsService, schemaService
} from 'shared/api/services/BEXO';

// Define the types for props in TabContent
interface TabContentProps {
    icon: React.ElementType;
    title: string;
    description: string;
    tags: string[];
    buttonText: string;
    buttonAction: () => void;
}

const TabContent: React.FC<TabContentProps> = ({
    icon: Icon,
    title,
    description,
    tags,
    buttonText,
    buttonAction
}) => (
    <Card className="w-full rounded h-full shadow-md bg-background overflow-hidden border">
        <CardContent className="p-0 flex flex-col md:flex-row items-center justify-center h-full w-full">
            <div className="h-full min-w-[200px] w-full md:w-1/3">
                <div className="bg-muted w-full h-full flex items-center justify-center p-6">
                    <Icon className="w-32 h-32 text-[#b71e3d]" />
                </div>
            </div>
            <div className="h-full min-w-[300px] w-full md:w-2/3">
                <div className="px-6 py-4">
                    <div className="font-bold text-xl mb-4 text-center md:text-left">{title}</div>
                    <p className="text-muted-foreground text-sm text-center md:text-left">
                        {description}
                    </p>
                </div>
                <div className="px-6 pt-4 pb-4 text-center md:text-left">
                    {tags.map((tag, index) => (
                        <span key={index} className="inline-block bg-secondary rounded-full px-3 py-1 text-xs font-semibold text-secondary-foreground mr-2 mb-2">
                            {tag}
                        </span>
                    ))}
                </div>
                <div className="px-6 pb-4 flex justify-center md:justify-start">
                    <Button className="w-fit" onClick={buttonAction}>
                        {buttonText}
                    </Button>
                </div>
            </div>
        </CardContent>
    </Card>
);

const DataBuilderDashboard: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const {
         oragID, appId
       } = useParams<{ oragID: string; appId: string }>();

    const [tables, setTables] = useState<any[]>([]);
    const [loadingTables, setLoadingTables] = useState<boolean>(false);
    const [activeTableCount, setActiveTableCount] = useState<number>(0);

    // fetchAllRecords function
    const fetchAllRecords = async () => {
        const payload = {
            target: 'user',
            action: 'search',
            filters: {
                logicalOperator: 'AND',
                rules: [
                    {
                        field: 'recordId',
                        dataType: 'tag',
                        comparisonOperator: 'notEquals',
                        value: 'null',
                    },
                ],
            },
            columnsToRetrieve: [],
            resultDisplayOrder: 'row',
            maxRowsPerPage: 20,
            pageToRetrieve: 1,
        };

        const response = await bexInsightsService.searchV2(oragID, appId, payload);
        const rawRows = response?.tableRow || [];

        const columnKeys = Object.keys(
            rawRows.reduce((acc, row) => ({
                ...acc,
                ...row
            }), {})
        );

        const columns = columnKeys.map((key) => ({
            id: key,
            name: key,
            type:
              typeof rawRows.find((r) => r[key] !== undefined)?.[key] === 'number'
                ? 'number'
                : 'text',
        }));

        const records = rawRows.map((row, i) => ({
            id: row.id || row.recordId || `row-${i}`,
            ...row,
        }));

        return {
            columns,
            records
        };
    };

    const fetchTables = async () => {
        setLoadingTables(true);
        // setError(null);
        try {
            const res = await schemaService.retrieveSchemas(null, appId, oragID);
            const schemas = res.data.schemas || [];

            setTables(schemas);
            localStorage.setItem('tables', JSON.stringify(schemas));

            // Count unique tableNames
            const uniqueTableNames = new Set(schemas.map((schema) => schema.tableName));
            setActiveTableCount(uniqueTableNames.size);

        } catch (error) {
            console.error('Error retrieving schemas:', error);

            const storedTables = localStorage.getItem('tables');
            if (storedTables) {
                const parsedTables = JSON.parse(storedTables);
                setTables(parsedTables);

                const uniqueTableNames = new Set(parsedTables.map((schema) => schema.tableName));
                setActiveTableCount(uniqueTableNames.size);
            }
        } finally {
            setLoadingTables(false);
        }
    };

    // Fetch tables on mount
    useEffect(() => {
        fetchTables();
    }, []);

    const handleNavigate = async (page: string) => {
        if (page === 'tables') {
            try {
                const data = await fetchAllRecords();
                // Pass columns, records, and tables to next page via location.state
                navigate(`${location.pathname}/${page}`, {
                    state: {
                        data,
                        tables,
                        activeTableCount
                    }
                });
            } catch (error) {
                console.error('Error fetching records before navigation', error);
                navigate(`${location.pathname}/${page}`, {
                    state: {
                        tables,
                        activeTableCount
                    }
                });
            }
        } else {
            navigate(`${location.pathname}/${page}`);
        }
    };

    const tabs = [
        {
            value: 'tables',
            label: 'Tables',
            description: 'Create and manage structured data tables efficiently.',
            icon: LayoutDashboard,
            content: (
                <TabContent
                    icon={LayoutDashboard}
                    title="Tables"
                    description="Easily create, organize, and manage structured datasets with our advanced table management system. Use dynamic column configurations, relations, and data validation to ensure high-quality, well-structured data."
                    tags={['#dataorganization', '#structureddata', '#tables']}
                    buttonText="Tables"
                    buttonAction={() => handleNavigate('tables')}
                />
            )
        }
    ];

    const scrollAreaRef = useRef<HTMLDivElement | null>(null);

    return (
        <PageLayout ref={scrollAreaRef}>
            <PageContent>
                <div className="text-center w-full">
                    <div className="pt-4">
                        <Tabs defaultValue={tabs[0].value} className="flex flex-row w-full">
                            <div className="w-1/2 flex flex-col pr-4">
                                <TabsList className="flex flex-col h-auto w-full space-y-2 p-1 p-2 bg-transparent">
                                    {tabs.map((tab) => (
                                        <TabsTrigger
                                            key={tab?.value}
                                            value={tab?.value}
                                            className="flex items-center justify-start px-4 py-2 w-full data-[state=active]"
                                        >
                                            <tab.icon className="w-8 h-8 mr-4"/>
                                            <div className="text-left">
                                                <div className="text-sm font-semibold">{tab.label}</div>
                                                <div className="text-xs text-muted-foreground">{tab.description}</div>
                                            </div>
                                        </TabsTrigger>
                                    ))}
                                </TabsList>
                            </div>
                            {tabs.map((tab) => (
                                <TabsContent key={tab.value} value={tab.value} className="flex-1 w-1/2">
                                    {tab.content}
                                    {tab.value === 'tables' && (
                                        <div className="mt-4 px-6">
                                            {loadingTables ? 'Loading...' : activeTableCount}
                                        </div>
                                    )}
                                </TabsContent>
                            ))}
                        </Tabs>
                    </div>
                </div>
            </PageContent>
        </PageLayout>
    );
};

export default DataBuilderDashboard;
